import { apiRequest } from './client.js';
import { setAccessToken, clearAccessToken } from '../auth/tokenStore.js';

/**
 * Auth API calls. These match the shapes in apps/api/src/routes/auth.ts.
 * Keep this file in sync with any changes to the /v1/auth/* contracts.
 */

export interface AuthOrganization {
  slug: string;
  legalName: string;
  countryCode: 'NG' | 'GH' | 'CD';
  baseCurrency: 'NGN' | 'GHS' | 'CDF' | 'USD';
  locale: string;
  timezone: string;
  status: string;
}

export interface AuthUser {
  id: string;
  email: string;
  role: 'owner' | 'admin' | 'hr' | 'finance' | 'employee' | 'it';
  organizationId: string;
  /**
   * Details about the user's org. Lets downstream UI (FX admin page,
   * money formatting, payroll deduction labels) read the base currency
   * and country without a separate query. Populated by both the login
   * response and GET /v1/users/me.
   */
  organization: AuthOrganization;
}

interface AuthResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  user: AuthUser;
}

export interface LoginArgs {
  orgSlug: string;
  email: string;
  password: string;
}

export interface RegisterOrgArgs {
  org: {
    slug: string;
    legalName: string;
    countryCode: 'NG' | 'GH' | 'CD';
    baseCurrency: 'NGN' | 'GHS' | 'CDF' | 'USD';
    locale: string;
    timezone: string;
  };
  owner: {
    email: string;
    password: string;
  };
}

export interface RegisterOrgResponse {
  organization: {
    id: string;
    slug: string;
    legalName: string;
    countryCode: string;
    baseCurrency: string;
  };
  owner: {
    email: string;
    role: string;
  };
}

export async function registerOrg(args: RegisterOrgArgs): Promise<RegisterOrgResponse> {
  // Register does NOT return an access token — the client follows up with
  // a login call to pick one up. This keeps the register flow stateless
  // from the server's perspective and matches what the CLI/curl does.
  return apiRequest<RegisterOrgResponse>('/v1/auth/register-org', {
    method: 'POST',
    body: args,
    unauthenticated: true,
    skipRefresh: true,
  });
}

export async function login(args: LoginArgs): Promise<AuthUser> {
  const res = await apiRequest<AuthResponse>('/v1/auth/login', {
    method: 'POST',
    body: args,
    unauthenticated: true,
    skipRefresh: true, // 401 here = bad credentials, not an expired token
  });
  setAccessToken(res.accessToken);
  return res.user;
}

/**
 * GET /v1/users/me — fetch the currently-authenticated user.
 *
 * Sprint 1 addition. Used by AuthContext after silentRefresh succeeds on
 * page reload, since POST /v1/auth/refresh only returns a new access
 * token (no user body). Without this, the user object stays null after
 * reload and the UI shows blanks in the header.
 *
 * Maps the API's fuller UserResponse shape to the smaller AuthUser the FE
 * needs for display — role + orgId for routing, email for the header.
 */
export async function getMe(): Promise<AuthUser> {
  const res = await apiRequest<{
    id: string;
    organizationId: string;
    email: string;
    role: AuthUser['role'];
    organization: AuthOrganization;
  }>('/v1/users/me');
  return {
    id: res.id,
    email: res.email,
    role: res.role,
    organizationId: res.organizationId,
    organization: res.organization,
  };
}

/**
 * POST /v1/auth/password/reset/request — request a reset email.
 *
 * Always 200 with `{ status: 'ok', message: ... }` regardless of
 * whether the (orgSlug, email) combo exists — server-side enumeration
 * defense. UI shows the same success message either way.
 */
export async function requestPasswordReset(input: {
  orgSlug: string;
  email: string;
}): Promise<{ status: 'ok'; message: string }> {
  return apiRequest<{ status: 'ok'; message: string }>(
    '/v1/auth/password/reset/request',
    {
      method: 'POST',
      body: input,
      unauthenticated: true,
      skipRefresh: true,
    },
  );
}

/**
 * POST /v1/auth/password/reset/confirm — finalise a reset using the
 * token from the email link. 204 on success (no body). Possible
 * errors (ApiError): `weak_password` (with `reason`), `invalid_token`
 * (every lifecycle failure maps here — don't try to distinguish them).
 */
export async function confirmPasswordReset(input: {
  token: string;
  newPassword: string;
}): Promise<void> {
  await apiRequest<void>('/v1/auth/password/reset/confirm', {
    method: 'POST',
    body: input,
    unauthenticated: true,
    skipRefresh: true,
  });
}

/**
 * GET /v1/auth/invitation/lookup?token=... — fetch invitation metadata so the
 * acceptance page can render "join <Org> as <Role>" before the user sets a
 * password. ApiError `invalid_token` covers every failure (expired / consumed /
 * superseded / not found) with one generic message — don't distinguish them.
 */
export interface InvitationLookupResult {
  email: string;
  role: AuthUser['role'];
  organizationLegalName: string;
  expiresAt: string;
}

export async function lookupInvitation(token: string): Promise<InvitationLookupResult> {
  return apiRequest<InvitationLookupResult>(
    `/v1/auth/invitation/lookup?token=${encodeURIComponent(token)}`,
    { unauthenticated: true, skipRefresh: true },
  );
}

/**
 * POST /v1/auth/invitation/accept — set the password for an invited account and
 * log in. Returns the AuthUser (the access token is stored, refresh cookie set
 * by the server). ApiError `weak_password` (with `reason`) or `invalid_token`.
 */
export async function acceptInvitation(input: {
  token: string;
  password: string;
}): Promise<AuthUser> {
  const res = await apiRequest<AuthResponse>('/v1/auth/invitation/accept', {
    method: 'POST',
    body: input,
    unauthenticated: true,
    skipRefresh: true,
  });
  setAccessToken(res.accessToken);
  return res.user;
}

export async function logout(): Promise<void> {
  try {
    // Server revokes the refresh token (cookie-based). 204 on success.
    await apiRequest<void>('/v1/auth/logout', {
      method: 'POST',
      skipRefresh: true,
    });
  } finally {
    // Always clear local state, even if the server call fails.
    clearAccessToken();
  }
}

/**
 * Try to hydrate an access token from the refresh cookie on app load.
 * Returns the user if refresh succeeded, null otherwise.
 *
 * NB: /v1/auth/refresh returns just { accessToken, ... } — no user object.
 * The user object was only sent on the original login response. For now
 * we return null-user on silent refresh and let the UI re-fetch
 * /v1/users/me when we add that endpoint (Sprint 1 TODO).
 */
export async function silentRefresh(): Promise<boolean> {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL ?? ''}/v1/auth/refresh`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      },
    );
    if (!res.ok) return false;
    const body = (await res.json()) as { accessToken?: string };
    if (!body.accessToken) return false;
    setAccessToken(body.accessToken);
    return true;
  } catch {
    return false;
  }
}
